import React, { useContext, useEffect, useState } from "react";
import { Box, Drawer, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import logoImg from "../assets/img/header/logo.svg";
import TestSkillsDialog from "../components/common/TestSkillsDialog";
import ProfileMenu from "../components/common/ProfileMenu";
import { getProfile } from "../apiCalls/user_profile/api_v1_profile";
import { loadActiveStreak } from "../apiCalls/streaks/api_v1_streaks";
import { refreshEasterWallet } from "../apiCalls/economy/api_v1_economy";
import { AuthContext } from "../apiCalls/jwt/AuthContext";
import {
  formFromProfile,
  mergeProfiles,
  readSessionProfile,
  saveProfileRecord,
  unwrapProfile,
} from "../utils/profileForm";
import { clearLegacyProfileLocalStorage } from "../utils/profileUserStore";
import {
  ga,
  cutCorner,
  EggWalletChip,
  LangSwitch,
  AvatarButton,
  getEggBalance,
  useUiLangState,
} from "../components/ui/GaPrimitives";

const NAV = [
  { key: "home", path: "/" },
  { key: "play", path: "/play" },
  { key: "videos", path: "/guides" },
  { key: "gameplay", path: "/bites" },
  // { key: "features", path: "/game" },
  { key: "rewards", path: "/rewards" },
  // { key: "proTips", path: "/tips" },
  { key: "news", path: "/news" },
];

function isActive(pathname, path) {
  if (path === "/") return pathname === "/" || pathname === "/home";
  if (path === "/news")
    return pathname.startsWith("/news") || pathname === "/article-view";
  if (path === "/guides") return pathname === "/guides";
  if (path === "/bites") return pathname === "/bites";
  if (path === "/game") return pathname === "/game" || pathname === "/shorts";
  return pathname === path || pathname.startsWith(`${path}/`);
}

function pageTitleFor(pathname, t) {
  if (pathname === "/article-view") return "ARTICLE";
  if (pathname === "/puzzle") return "PUZZLE MANIA";
  if (pathname === "/guides") return "PODCAST";
  if (pathname === "/challenges") return "TODAY'S CHALLENGES";
  if (pathname === "/terms") return "TERMS OF SERVICE";
  if (pathname === "/thankyou") return t.chrome.page["/rewards"] || "REWARDS";
  if (pathname === "/submit-redeem") return t.chrome.page["/rewards"] || "REWARDS";
  if (t.chrome.page[pathname]) return t.chrome.page[pathname];
  if (pathname.startsWith("/news")) return t.chrome.page["/news"];
  if (pathname === "/home") return t.chrome.page["/"];
  if (pathname === "/shorts") return t.chrome.page["/shorts"];
  return t.chrome.home?.toUpperCase?.() || "HOME";
}

const LayoutDesktop = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const { lang, switchLang, t } = useUiLangState();
  const [openTestSkills, setOpenTestSkills] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Seed from session immediately so header avatar has no first-paint delay.
  const [profile, setProfile] = useState(() => {
    try {
      const session = readSessionProfile();
      return session ? formFromProfile(session) : null;
    } catch {
      return null;
    }
  });
  const [titleOverride, setTitleOverride] = useState("");
  const hideChrome = location.pathname === "/ga-fps-game";
  const isHome = location.pathname === "/" || location.pathname === "/home";
  const fullBleed =
    location.pathname === "/tips" ||
    location.pathname === "/stories" ||
    location.pathname === "/ga-fps-game";

  useEffect(() => {
    const onTitle = (event) => setTitleOverride(event.detail || "");
    window.addEventListener("ga-page-title", onTitle);
    return () => window.removeEventListener("ga-page-title", onTitle);
  }, []);

  useEffect(() => {
    setTitleOverride("");
  }, [location.pathname]);

  useEffect(() => {
    if (!location.state?.openProfileMenu) return undefined;
    navigate("/profile", { replace: true });
    return undefined;
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    let cancelled = false;
    clearLegacyProfileLocalStorage();

    const applySession = () => {
      try {
        const sessionProfile = readSessionProfile();
        if (!sessionProfile || cancelled) return;
        // Normalize so avatar_index always resolves to profileImage (mobile parity).
        setProfile(formFromProfile(sessionProfile));
      } catch {
        /* keep the current profile */
      }
    };

    applySession();

    // Wait for a token so we do not fire profile/streak as 401 then retry.
    const token = auth?.accessToken || sessionStorage.getItem("accessToken");
    if (!token) {
      const handleProfileUpdate = () => applySession();
      window.addEventListener("profileUpdated", handleProfileUpdate);
      return () => {
        cancelled = true;
        window.removeEventListener("profileUpdated", handleProfileUpdate);
      };
    }

    (async () => {
      try {
        // Use session cache when fresh — avoids duplicate /user/profile on Play etc.
        const fetched = await getProfile();
        if (cancelled) return;
        const apiProfile = unwrapProfile(fetched);
        if (apiProfile) {
          const profileData = saveProfileRecord(
            mergeProfiles(apiProfile, readSessionProfile()),
            { notify: false },
          );
          setProfile(profileData);
        }
      } catch (e) {
        console.error("Failed to load profile", e);
        applySession();
      }

      try {
        await loadActiveStreak();
      } catch (error) {
        console.error("Active streak load failed:", error);
      }

      applySession();
    })();

    const handleProfileUpdate = () => applySession();
    window.addEventListener("profileUpdated", handleProfileUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("profileUpdated", handleProfileUpdate);
    };
  }, [auth?.accessToken]);

  // Wallet chip is on every chrome page — refresh /economy/easters on each route.
  useEffect(() => {
    if (hideChrome) return undefined;
    if (!sessionStorage.getItem("accessToken")) return undefined;

    let cancelled = false;
    (async () => {
      try {
        await refreshEasterWallet();
        if (cancelled) return;
        const sessionProfile = readSessionProfile();
        if (sessionProfile) setProfile(formFromProfile(sessionProfile));
      } catch (error) {
        console.error("Easter wallet refresh failed:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, hideChrome]);

  const eggCount = getEggBalance(profile);

  const handleBack = () => {
    if (location.state?.fromProfileMenu) {
      navigate("/", { state: { openProfileMenu: true } });
      return;
    }
    if (isHome) return;
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <Box
      sx={{
        height: "100%",
        maxHeight: "100dvh",
        display: "flex",
        bgcolor: ga.bg,
        overflow: "hidden",
      }}
    >
      {!hideChrome && (
        <Box
          component="nav"
          aria-label="Desktop"
          sx={{
            width: 220,
            flexShrink: 0,
            height: "100%",
            bgcolor: ga.bg,
            borderRight: `1px solid ${ga.border}`,
            display: "flex",
            flexDirection: "column",
            px: "22px",
            pt: { md: 3, lg: 4 },
            pb: 3,
            boxSizing: "border-box",
            overflowY: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          <Box
            onClick={() => navigate("/")}
            sx={{
              width: 105,
              height: 40,
              p: "8px 9px",
              boxSizing: "border-box",
              bgcolor: ga.lime,
              borderRadius: "2px",
              cursor: "pointer",
              flexShrink: 0,
              mb: "60px",
            }}
          >
            <Box
              component="img"
              src={logoImg}
              alt="GamezAcademy"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                filter: "brightness(0)",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {NAV.map((item) => {
              const active = isActive(location.pathname, item.path);
              return (
                <Box
                  key={item.key}
                  component="button"
                  type="button"
                  aria-label={t.chrome[item.key]}
                  aria-current={active ? "page" : undefined}
                  onClick={() => navigate(item.path)}
                  sx={{
                    width: "100%",
                    height: 44,
                    display: "flex",
                    alignItems: "center",
                    px: { md: "14px", lg: "18px" },
                    boxSizing: "border-box",
                    cursor: "pointer",
                    bgcolor: active ? ga.lime : "transparent",
                    clipPath: active ? cutCorner(10) : "none",
                    border: "none",
                    textAlign: "left",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: active ? 700 : 500,
                      fontSize: { md: 14, lg: 15 },
                      letterSpacing: active ? "0.4px" : 0,
                      color: active ? ga.bg : ga.muted,
                      lineHeight: 1.2,
                    }}
                  >
                    {t.chrome[item.key]}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              mt: "20px",
              width: "100%",
              height: "1px",
              bgcolor: ga.border,
            }}
          />

          <Box sx={{ flex: 1 }} />
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 400,
              fontSize: 11,
              color: ga.muted,
            }}
          >
            {t.footerLegal}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: ga.bg,
        }}
      >
        {!hideChrome && (
          <Box
            sx={{
              height: { md: 64, lg: 76 },
              px: "20px",
              display: "flex",
              alignItems: "center",
              gap: { md: 1.5, lg: 2 },
              borderBottom: `1px solid ${ga.border}`,
              bgcolor: ga.bg,
              flexShrink: 0,
              zIndex: 20,
              minWidth: 0,
            }}
          >
            {!isHome && (
              <Box
                component="button"
                type="button"
                aria-label={t.chrome.back}
                onClick={handleBack}
                sx={{
                  width: { md: 78, lg: 92 },
                  height: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1px solid ${ga.border}`,
                  bgcolor: "transparent",
                  clipPath: cutCorner(8),
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: "1px",
                    color: ga.muted,
                  }}
                >
                  ‹  {t.chrome.back}
                </Typography>
              </Box>
            )}

            <Typography
              sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: { md: 18, lg: 22, xl: 24 },
                letterSpacing: "2px",
                color: ga.text,
                lineHeight: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {titleOverride || pageTitleFor(location.pathname, t)}
            </Typography>

            <Box sx={{ flex: 1 }} />
            <LangSwitch lang={lang} switchLang={switchLang} />
            <EggWalletChip count={eggCount} onClick={() => navigate("/rewards")} />
            <AvatarButton
              src={profile?.profileImage}
              onClick={() => navigate("/profile")}
              size={44}
            />
          </Box>
        )}

        <Box
          id="main-scroll-container"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow:
              location.pathname === "/tips" || location.pathname === "/stories"
                ? "hidden"
                : "auto",
            bgcolor:
              location.pathname === "/tips" || location.pathname === "/stories"
                ? "transparent"
                : ga.bg,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          <Box
            sx={{
              width: "100%",
              minHeight: fullBleed ? "100%" : "auto",
              height: fullBleed ? "100%" : "auto",
              boxSizing: "border-box",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        PaperProps={{
          sx: { width: 420, maxWidth: "100vw", bgcolor: ga.bg },
        }}
      >
        <ProfileMenu
          onClose={() => setIsSidebarOpen(false)}
          lang={lang}
          switchLang={switchLang}
          profile={profile}
        />
      </Drawer>

      <TestSkillsDialog
        open={openTestSkills}
        onClose={() => setOpenTestSkills(false)}
      />
    </Box>
  );
};

export default LayoutDesktop;
